import { map, sortBy } from "lodash";
import { createSelector } from "reselect";
import { Map } from "immutable";

import { PRIORITY_COUNTRIES } from "../constants";
import {
    LocalisationState as State,
    Locale,
    Country,
    IRegion,
    Region,
    LocalisationNormalisedEntities,
} from "../models";
import { Localisation } from "../index";
import { getInitialState } from "../reducers";

import * as messagesEn from "@app/resources/translations/en";
import * as messagesFr from "@app/resources/translations/fr";

export const getLanguage = (state: State) => state.localisation.language;
export const getEntities = (state: State) =>
    state.localisation.entities ? state.localisation.entities : getInitialState().entities;

export const getCountries = createSelector(
    getEntities,
    (entities: LocalisationNormalisedEntities) => entities.countries,
);

export const getRegions = createSelector(
    getEntities,
    (entities: LocalisationNormalisedEntities) => entities.regions,
);

export const getCountriesArray = createSelector(
    getCountries,
    (countries: Map<string, Country>) => countries.toArray(),
);

export const getLocale = createSelector(
    getLanguage,
    (language: string) => {
        const locale = Localisation.stripRegionFromLanguage(language);
        if (Localisation.validLocale(locale)) {
            return locale as Locale;
        }
        else {
            return "en";
        }
    },
);

export const getMessages = createSelector(
    getLocale,
    (locale: Locale) => {
        if (locale === "fr") {
            return messagesFr.default;
        }
        else {
            return messagesEn.default;
        }
    },
);

export const getCountriesArrayForSelect = createSelector(
    getCountriesArray,
    getLocale,
    (countries: Country[], locale: Locale) => {
        const priorities: Country[] = [];
        const others: Country[] = [];
        for (const country of countries) {
            if (PRIORITY_COUNTRIES.indexOf(country.code.toUpperCase()) > -1) {
                priorities.push(country);
            }
            else {
                others.push(country);
            }
        }
        return [
            {
                options: sortBy(priorities, (country) => country.printableName[locale]),
            },
            {
                label: "—",
                options: sortBy(others, (country) => country.printableName[locale]),
            },
        ];
    },
);

export const getCountriesForSelect = createSelector(
    getCountriesArray,
    getLocale,
    (countries: Country[], locale: Locale) => sortBy(
        map(
            countries,
            (country) => ({
                value: country.code,
                label: Localisation.translateMap(country.printableName, locale),
            }),
        ), "label",
    ),
);

export const makeHydrateRegion = (entities: LocalisationNormalisedEntities) => (e: Region | string): Region => {
    // if it's an ID, get it from the state.
    if (typeof e === "string") {
        e = entities.regions.get(e);
    }

    if (e == null) {
        // Nothing to hydrate
        return e as Region;
    }

    // The artwork
    const region = {} as IRegion;
    if (e.countryCode != null && entities.countries.has(e.countryCode)) {
        region.country = entities.countries.get(e.countryCode);
    }

    // And the computed data
    e = e.set("hydrated", true) as Region;

    return e.merge(region) as Region;
};
